import { RequestService } from '@df/api/request/request.service';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { IAddressData } from '@df/session/api/address-book/address';
import { Nexus } from '@tomandco/nexus-api-types';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import isArray from 'lodash-es/isArray';
import { Subject, Subscription } from 'rxjs';

export interface IHint {
    Title?: string;
    Description?: string;
}

export interface IAddressSearcherData {
    data: IAddressData;
    regions?: Nexus.Lib.General.Region[];
    //
    detectChanges: () => void;
}

export interface IAddressSearcherOptions {
    runOnQueryChange: boolean;
}

export const DEFAULT_ADDRESS_SEARCHER_OPTIONS: IAddressSearcherOptions = {
    runOnQueryChange: true
};

export abstract class AddressSearcher {
    selectionApplied = new Subject<void>();

    private subscriptions = new Subscription();

    /**
     * Flag if form should be visible (forced mode)
     * @type {boolean}
     */
    isManual = false;

    /**
     * Hints array (those can be addresses etc)
     * @type {IHint[]}
     */
    protected _hints?: IHint[];
    set hints(value: IHint[] | undefined) {
        this._hints = value;

        this.addressSearcherData.detectChanges();
    }
    get hints(): IHint[] | undefined {
        return this._hints;
    }

    /**
     * Searched query string
     * @type {string}
     */
    protected _query?: string;

    status = new Status();

    @NgInject(TimeoutService)
    timeoutService!: TimeoutService;

    @NgInject(RequestService)
    requestService!: RequestService;

    search: (...args: any[]) => void;

    constructor(
        protected addressSearcherData: IAddressSearcherData,
        protected options = DEFAULT_ADDRESS_SEARCHER_OPTIONS
    ) {
        this.subscriptions.add(this.status.subscribe(() => this.addressSearcherData.detectChanges()));
        this.search = this.timeoutService.debounce((...args: any[]) => this._search(...args), 1000);
    }

    onDestroy() {
        this.query = undefined;
        this.subscriptions.unsubscribe();
    }

    set query(value: string | undefined) {
        this._query = value;
        if (this.options.runOnQueryChange) {
            this.search();
        }
    }

    get query(): string | undefined {
        return this._query;
    }

    /**
     * Sends request to BE for hints
     */
    protected _search(...args: any[]) {
        // cancel previous search
        if (this.status.promise && this.status.promise.cancel) {
            this.status.promise.cancel();
            this.status.reset();
        }

        // run query if avabile
        if (this.isAvailable && this.query && this.query.length > 3) {
            this.runSearch(...args);
        } else {
            this.clear();
        }
    }

    /**
     * Abstract method that should process query using status
     */
    protected abstract runSearch(...args: any[]): void;

    /**
     * Flag if service is currently availble
     * @type {boolean}
     */
    get isAvailable(): boolean {
        return true;
    }

    clear(): void {
        this.hints = [];
    }

    protected applySelection(...args: any[]) {
        this.isManual = true;
        this.clear();
        this.selectionApplied.next();
    }

    protected pushTo(target: string, source: string | string[], existing?: string[]): string {
        // call recurently if array
        if (isArray(source)) {
            source.forEach(item => {
                target = this.pushTo(target, item, existing);
            });
            return target;
        }
        if (!existing) {
            existing = [];
        }
        existing.push(target);
        // replace if string does not belong to any of sources
        if (
            source &&
            !find(existing, existingTarget => {
                return existingTarget.indexOf(source) !== -1;
            })
        ) {
            return compact([source, target]).join(', ');
        }
        return target;
    }

    abstract trackByHint(index: number, hint: IHint);
}
